// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import numeral from 'numeral';
import { rgba } from 'polished';
import t from 'app/utils/translate';
import AnimateNumber from '../commons/AnimateNumber';

type Props = {
  className?: string,
  currencySymbol: string,
  entryCount?: number,
  prize?: string | number,
  rank?: number,
  score: number,
  scoreProjected?: number,
  showProjected?: boolean
};

const defaultProps = {
  className: undefined,
  entryCount: 0,
  prize: '0',
  rank: undefined,
  scoreProjected: undefined,
  showProjected: false
};

const Wrapper = styled.div(({ theme }) => ({
  alignItems: 'stretch',
  display: 'flex',
  fontSize: theme.fontSize.small,
  justifyContent: 'space-between'
}));

const Col = styled.div(({ theme }) => ({
  alignItems: 'stretch',
  borderColor: rgba(theme.color.white, 0.25),
  borderStyle: 'solid',
  borderWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  flexBasis: 'auto',
  flexGrow: 1,
  flexShrink: 1,
  fontWeight: theme.fontWeight.normal,
  textAlign: 'center',
  '&:not(:first-of-type)': {
    borderLeftWidth: 1
  }
}));

const Subheading = styled.div(({ theme }) => ({
  color: theme.color.white,
  fontSize: '0.75em',
  fontWeight: theme.fontWeight.demiBold,
  paddingTop: 3,
  paddingRight: 5,
  paddingBottom: 3,
  paddingLeft: 5,
  textTransform: 'uppercase'
}));

const Cell = styled.div(({ theme }) => ({
  background: theme.color.white,
  color: theme.color.basis2,
  fontWeight: theme.fontWeight.demiBold,
  justifyContent: 'center',
  paddingTop: 3,
  paddingRight: 5,
  paddingBottom: 3,
  paddingLeft: 5,
  textAlign: 'center'
}));

function UserStatsCols({
  className,
  currencySymbol,
  entryCount,
  prize,
  rank,
  score,
  scoreProjected,
  showProjected
}: Props) {
  return (
    <Wrapper className={className}>
      {rank && (
        <Col>
          <Subheading>{t('Position')}</Subheading>
          <Cell>
            {rank}
            {entryCount > 0 && (
              <span>
                {' / '}
                {entryCount}
              </span>
            )}
          </Cell>
        </Col>
      )}
      <Col>
        <Subheading>{t('Win')}</Subheading>
        <Cell>{numeral(prize).format(`${currencySymbol}0,0.00`)}</Cell>
      </Col>
      {showProjected && (
        <Col>
          <Subheading>{t('Projected')}</Subheading>
          <Cell>
            <AnimateNumber number={scoreProjected} format="0.00" />
          </Cell>
        </Col>
      )}
      <Col>
        <Subheading>{t('Points')}</Subheading>
        <Cell>
          <AnimateNumber number={score} format="0.00" />
        </Cell>
      </Col>
    </Wrapper>
  );
}

UserStatsCols.defaultProps = defaultProps;

const connector = connect(
  state => ({
    currencySymbol: state.settings.currency.symbol || ''
  }),
  null
);

export default connector(UserStatsCols);
